/**
 * Toast propio del rediseño — sustituye al de `react-notifications-component`
 * que traía la plantilla EduChamp y se veía como notificación de escritorio
 * genérica encima del navbar.
 *
 * Se inyecta un contenedor único al `<body>` la primera vez que se dispara
 * un toast (idempotente) y cada llamada añade una tarjeta que sale
 * desde la esquina superior derecha con fade-in + slide-in y se retira
 * sola a los `duration` ms (default 4.2 s). Tap en la card la cierra
 * antes.
 *
 * Estilo alineado con los tokens del sistema: card blanca radio 14, sombra
 * navy suave, indicador lateral por tipo (success verde / accent naranja /
 * secondary cyan / danger rojo).
 */

const CONTAINER_ID = "academy-toast-container";
const CONTAINER_STYLE = {
  position: "fixed",
  top: 88, // debajo del AppTopNav (72) con margen
  right: 18,
  zIndex: 100,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  maxWidth: "min(360px, calc(100vw - 32px))",
  pointerEvents: "none",
};

const TONE_MAP = {
  success: {
    border: "var(--color-success)",
    icon: "M5 12.5l4 4 8-9",
    bg: "#E3F5EA",
    fg: "var(--color-success)",
  },
  info: {
    border: "var(--color-secondary-strong)",
    icon: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    bg: "#E6F6FB",
    fg: "var(--color-secondary-strong)",
  },
  warning: {
    border: "#EC7137",
    icon: "M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.75-2.98L13.75 5.02a2 2 0 00-3.5 0L3.18 16.02A2 2 0 004.93 19z",
    bg: "#FDEBDC",
    fg: "#8A4102",
  },
  danger: {
    border: "#C93D3D",
    icon: "M6 6l12 12M6 18L18 6",
    bg: "#FBE7E7",
    fg: "#8A1F1F",
  },
};

const ensureContainer = () => {
  let el = document.getElementById(CONTAINER_ID);
  if (el) return el;
  el = document.createElement("div");
  el.id = CONTAINER_ID;
  Object.assign(el.style, CONTAINER_STYLE);
  // Cada card gestiona su propio pointer-events: auto — el contenedor
  // vive con pointer-events:none para no bloquear la UI de fondo.
  document.body.appendChild(el);
  return el;
};

const ensureKeyframes = () => {
  const KF_ID = "academy-toast-keyframes";
  if (document.getElementById(KF_ID)) return;
  const style = document.createElement("style");
  style.id = KF_ID;
  style.textContent = `
    @keyframes academyToastIn {
      from { opacity: 0; transform: translateX(24px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes academyToastOut {
      from { opacity: 1; transform: translateX(0); }
      to   { opacity: 0; transform: translateX(24px); }
    }
    @media (prefers-reduced-motion: reduce) {
      .academy-toast { animation: none !important; }
    }
  `;
  document.head.appendChild(style);
};

const showToast = (text, { tone = "info", duration = 4200 } = {}) => {
  if (typeof document === "undefined") return; // SSR / tests sin DOM
  ensureKeyframes();
  const container = ensureContainer();
  const T = TONE_MAP[tone] ?? TONE_MAP.info;

  const card = document.createElement("div");
  card.className = "academy-toast";
  card.setAttribute("role", tone === "danger" ? "alert" : "status");
  Object.assign(card.style, {
    pointerEvents: "auto",
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    background: "var(--color-surface, #fff)",
    border: "1px solid var(--color-line, #E2E6EE)",
    borderLeft: `4px solid ${T.border}`,
    borderRadius: 14,
    padding: "13px 15px",
    boxShadow: "0 12px 32px rgba(11, 20, 36, 0.14)",
    fontFamily: "inherit",
    animation: "academyToastIn 220ms cubic-bezier(0.2,0.8,0.2,1)",
    cursor: "pointer",
  });

  // Icono (SVG plano — evita depender de iconify aquí)
  const iconWrap = document.createElement("span");
  Object.assign(iconWrap.style, {
    flex: "0 0 auto",
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: T.bg,
    color: T.fg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  });
  iconWrap.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="${T.icon}"/></svg>`;
  card.appendChild(iconWrap);

  const msg = document.createElement("div");
  Object.assign(msg.style, {
    flex: 1,
    minWidth: 0,
    fontSize: 14.5,
    lineHeight: 1.45,
    fontWeight: 700,
    color: "var(--color-ink, #101C36)",
  });
  msg.textContent = text;
  card.appendChild(msg);

  const remove = () => {
    card.style.animation = "academyToastOut 180ms ease-out forwards";
    setTimeout(() => card.remove(), 200);
  };
  card.addEventListener("click", remove);
  const timer = setTimeout(remove, duration);
  card.addEventListener("mouseenter", () => clearTimeout(timer));

  container.appendChild(card);
};

/* API legada — el resto del proyecto sigue usando estos nombres. */
export const alertNotification = (text) => showToast(text, { tone: "danger" });

export const prospectNotification = (text, state) =>
  showToast(text, {
    tone: state === "info" ? "success" : state ?? "info",
  });
